/* Copyright (c) 2020-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Subject } from 'rxjs';
import { SsoIframeMessageFormat } from 'crimson/lib/silentLogin/constants/constants';
import { SilentLoginHelperClass } from 'crimson/lib/silentLogin/services/silentLoginAPI';
import { OpenTelemetrySpan } from 'distributed-tracing/lib/distributed-tracing';

export interface StarfleetUser {
    sub: string;
    idp_name: string;
    idp_id: string;
    external_id: string;
    preferred_username: string;
    consent: {
        trackTechnicalData: boolean;
        trackBehavioralData: boolean;
        targetedAdsOptIn?: boolean;
    };
    isAuthenticated: boolean;
    email?: string;
    age?: number;
    isNetworkCall?: boolean;
}

export interface StarfleetUserChange {
    newStarfleetUser: Readonly<StarfleetUser>;
    oldStarfleetUser: Readonly<StarfleetUser>;
}

export enum StarfleetTransitionState {
    LoginAlreadyInProgress = 'Login Already In Progress',
    OpenOAuthUrlStarted = 'Open OAuth url started',
    OpenOAuthUrlCompleted = 'Open OAuth url completed',
    LoginStarted = 'Login Started', // indicating that data is received from starfleet
    // and we are getting user info, etc
    LoginCompleted = 'Login Completed',
    LoginRedirectCompleted = 'Login redirect completed',
    LoginAbortedByUser = 'Login Aborted: User Cancelled Login',
    LoginAbortedAlreadyLoggedIn = 'Login Aborted: Already Logged In',
    LoginFailed = 'Login Failed',
    // login has failed with no option to retry
    LoginFailedNoRetry = 'Login Failed No Retry',
    LoginFailedToStart = 'Login failed to Start',
    LogoutStarted = 'Logout Started',
    LogoutCompleted = 'Logout Completed',
    LogoutFailed = 'Logout Failed',
    // Device code stuff
    LoginInProgressNeedDisplayCode = 'Login Started Need Display Code',
    // Same Tab Login State
    LoginNotReturningFromLoginPage = 'Login not returning from login page'
}

export enum AuthErrors {
    NOT_LOGGED_IN = 'Not Logged In, Must login',
    USER_OFFLINE = 'User is Offline',
    ALREADY_LOGGED_IN = 'Already logged in',
    AUTH_ERROR = 'Auth Error',
    AGE_RESTRICTED = 'age_restricted',
    ACCOUNT_LOCKED = 'account_locked',
    HTTP_START_FAILED = 'Http Server start failed',
    CLIENT_TOKEN_FAIL = 'Client token url access failed',
    OAUTH_DATA_EMPTY = 'JSON parsing fialed for OAUTH Data',
    STARFLEET_REDIRECT_FAIL = 'Failure on Starfleet Redirect Page',
    REDEEM_AUTHCODE_FAILED = 'Failuer in redeeming auth code',
    NONCE_VALIDATION_FAILED = 'Nonce validation failed',
    ACCOUNT_INACTIVE = 'account_inactive',
    LOGIN_CANCELLED_BY_USER = 'User pressed back from login page',
    // Logout Errors
    LOGOUT_NOT_SUPPORTED = 'logout_not_supported',
    LOGOUT_GENERIC_ERROR = 'logout_generic_error',
    // SSO Errors
    LOGIN_SILENT_LOGIN_FAILED = 'silent_login_failed'
}

export interface AuthStatus {
    authReturnState: StarfleetTransitionState;
    error: AuthErrors;
    logoutNotSupportedRedirectUrl?: string;
}

export interface StarfleetTransition {
    isLoading: boolean;
    state: StarfleetTransitionState;
}

export interface StarfleetTransitionFailed extends StarfleetTransition {
    error: any;
}

export interface ClientTokenResponse {
    expires_in: number;
    client_token: string;
}

export interface AcessTokenRequest {
    grant_type: string;
    client_token: string;
    client_id: string;
    sub: string;
}

export interface OAuthLoginData {
    error?: string;
    error_description?: string;
    access_token?: string;
    token_type?: string;
    expires_in?: number;
    id_token?: string;
    code?: string;
    error_uri?: string;
    oAuthUrl?: OAuthUrl;
}

export interface AccessTokenResponse {
    error: string;
    access_token: string;
    token_type: string;
    expires_in: number;
    client_token: string;
    id_token: string;
}

export interface DeviceAuthorizeResponse {
    user_code: string;
    device_code: string;
    verification_uri: string;
    verification_uri_complete: string;
    expires_in: number;
    interval: number;
}

export interface DeviceDisplayCodeData {
    url: string;
    url_complete: string;
    user_code: string;
}

export interface SessionData {
    clientToken: string;
    accessToken: string;
    idToken: string;
    user: StarfleetUser;
    clientTokenExpiry: number;
    accessTokenExpiry: number;
    idTokenExpiry: number;
    clientTokenExpiryLength?: number;
}

// KV store
export interface KVStoreClientDataResponse {
    data: [
        {
            key: string;
            value: [string];
        }
    ];
}

export interface ErrorInfo {
    error_code: string;
    error_description: string;
    error_uri: string;
}

export interface OAuthUrl {
    url: string;
    redirectUrl: string;
    codeVerifier: string;
    nonce?: string;
    span?: OpenTelemetrySpan;
    oAuthWindow?: Window;
    returnUrl?: string;
    ifrmEl?: HTMLIFrameElement;
}

export interface IdTokenData {
    sub: string;
    idp_name: string;
    idp_id: string;
    iss: string;
    external_id: string;
    preferred_username: string;
    consent: {
        trackBehavioralData: boolean;
        opt_in: boolean;
        trackTechnicalData: boolean;
        targetedAdsOptIn?: boolean;
    };
    nonce: string;
    sid: string;
    aud: [string];
    updated_at: number;
    azp: string;
    exp: number;
    iat: number;
    jti: string;
}

export interface SilentLoginInfo {
    ssoSharedPageData: SsoIframeMessageFormat;
    silentLoginHelperInstance: SilentLoginHelperClass;
}

export interface LoginOptions {
    force?: boolean;
    idpId?: string;
    preCreatedWindow?: Window;
    deviceName?: string;
    returnLocationUrl?: string;
    silentLoginInfo?: SilentLoginInfo;
    redirect?: {
        url: string;
        observable: Subject<string>;
    };
}
